import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import CardContact from "../components/CardContact";
import CardInfoACompleter from "../components/CardInfoACompleter";
import CardRdv from "../components/CardRdv";
import CardAvancementDocument from "../components/CardAvancementDocument";
import RdvAffaire from "../components/RdvAffaire";
import InfosBasiqueAffaire from "../components/InfosBasiqueAffaire";


// helpers
function sortBySortField(records) {
  const arr = Array.isArray(records) ? [...records] : [];
  arr.sort((a, b) => ((a?.fields?.sort ?? 999) < (b?.fields?.sort ?? 999) ? -1 : 1));
  return arr;
}

function firstMatchByNom(records, nom) {
  if (!Array.isArray(records)) return null;
  const lower = nom.toLowerCase();
  return records.find((r) => String(r?.fields?.nom || "").trim().toLowerCase() === lower) ?? null;
}

function TabsAffaireInformations({
  affaire,
  type,
  onOpenDocuments
}) {
  const [event, setEvent] = useState(null);
  const [action, setAction] = useState(null);
  const [rdv, setRdv] = useState(null);
  const [demandeRdv, setDemandeRdv] = useState(null);

  const isMountedRef = useRef(true);

  const affaireId = affaire?.id;

  const AIRTABLE_TOKEN =
    "Bearer patfRIUbOM9xqwLV2.dfbc9a305f2124aff75634c819a8335ecd984b1d19e98f67f14013378ed6bb02";
  const BASE = "appD48APNaGA4GN0B";

  // ✅ Mapping : route type -> champ Airtable utilisé dans event/rdv/actions
  const airtableFieldByType = useMemo(
    () => ({
      immo: "transaction",
      pma: "pma",
      mariage: "mariage",
      pacs: "pacs",
      certification: "certification",
      bail: "bail",
      donation: "donation",
      divorce: "divorce",
      aucune_demarche: "aucune_demarche",
    succession: "succession",
      // routes qui lisent "autre_demarche" côté page Affaire
      
      testament: "autre_demarche",
      adoption: "autre_demarche",
      "statut-societe": "autre_demarche",
      procuration: "autre_demarche",
    }),
    []
  );

  const headers = useMemo(
    () => ({
      Authorization: AIRTABLE_TOKEN,
      Accept: "application/json",
    }),
    []
  );

  const fetchAirtable = useCallback(
    async (table, filterByFormula) => {
      const url = `https://api.airtable.com/v0/${BASE}/${table}?filterByFormula=${encodeURIComponent(
        filterByFormula
      )}`;

      const res = await fetch(url, { method: "GET", headers });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`[Airtable] ${table} HTTP ${res.status} - ${txt}`);
      }

      const json = await res.json();
      return json?.records ?? [];
    },
    [BASE, headers]
  );

  // ======================
  // GETTERS
  // ======================

  // ✅ getEvent
  const getEvent = useCallback(async () => {
    if (!affaireId) return;

    const airtableField = airtableFieldByType[type];
    if (!airtableField) return;

    try {
      const filter = `SEARCH("${affaireId}",{${airtableField}})`;
      const records = await fetchAirtable("event", filter);
      if (!isMountedRef.current) return;
      setEvent(records ?? []);
    } catch (e) {
      console.error("getEvent error", e);
    }
  }, [affaireId, type, airtableFieldByType, fetchAirtable]);

  // ✅ getActions
  const getActions = useCallback(async () => {
    if (!affaireId) return;

    const airtableField = airtableFieldByType[type];
    if (!airtableField) return;

    try {
      const filter = `SEARCH("${affaireId}",{${airtableField}})`;
      const records = await fetchAirtable("actions", filter);
      if (!isMountedRef.current) return;
      setAction(records ?? []);
    } catch (e) {
      console.error("getActions error", e);
    }
  }, [affaireId, type, airtableFieldByType, fetchAirtable]);

  // ✅ getRdv
  const getRdv = useCallback(async () => {
    if (!affaireId) return;

    const airtableField = airtableFieldByType[type];
    if (!airtableField) return;

    try {
      const filter = `SEARCH("${affaireId}",{${airtableField}})`;
      const records = await fetchAirtable("rdv", filter);
      if (!isMountedRef.current) return;

      const sorted = sortBySortField(records);
      setRdv(sorted);
    } catch (e) {
      console.error("getRdv error", e);
    }
  }, [affaireId, type, airtableFieldByType, fetchAirtable]);

  // ✅ getDemandeRdv
  const getDemandeRdv = useCallback(async () => {
    if (!affaireId) return;

    try {
      // NOTE: ici tu filtres par {id_affaire} (comme dans ton code immo)
      const filter = `SEARCH("${affaireId}",{id_affaire})`;
      const records = await fetchAirtable("demande_rdv", filter);
      if (!isMountedRef.current) return;

      const sorted = sortBySortField(records);
      setDemandeRdv(sorted);
    } catch (e) {
      console.error("getDemandeRdv error", e);
    }
  }, [affaireId, fetchAirtable]);

  // ======================
  // INITIAL LOAD
  // ======================
  useEffect(() => {
    isMountedRef.current = true;

    if (!affaireId) return () => { isMountedRef.current = false; };

    // style "immo": on déclenche tout
    getEvent();
    getRdv();
    getActions();
    getDemandeRdv();

    return () => {
      isMountedRef.current = false;
    };
  }, [affaireId, getEvent, getRdv, getActions, getDemandeRdv]);

  // ✅ Refresh commun (même signature que ton immo)
  const refreshAll = useCallback(async () => {
    await Promise.all([getRdv(), getDemandeRdv(), getActions(), getEvent()]);
  }, [getRdv, getDemandeRdv, getActions, getEvent]);

  // ======================
  // DERIVED (optionnel)
  // ======================
  const rdvNotaire = useMemo(() => firstMatchByNom(rdv, "Rendez-vous notaire"), [rdv]);
  const rdvSignature = useMemo(() => firstMatchByNom(rdv, "Rendez-vous de Signature"), [rdv]);


  if (event !== null && rdv !== null && action !== null) {
  return (
    <>
      {/* info basique */}
      <InfosBasiqueAffaire type={type} affaire={affaire} />

      {/* contact client */}
      <div>
        <h5 className="flex-auto truncate text-sm font-semibold leading-6 text-gray-700">
          <b>Contact(s) client</b>
        </h5>

        <CardContact
          nom={affaire?.nom_from_user?.[0] ?? affaire?.nom_from_acheteur?.[0]}
          prenom={affaire?.prenom_from_user?.[0] ?? affaire?.prenom_from_acheteur?.[0]}
          telephone={affaire?.telephone_from_user?.[0] ?? affaire?.telephone_from_acheteur?.[0]}
          email={affaire?.email_from_user?.[0] ?? affaire?.email_from_acheteur?.[0]}
          label="client"
        />

        {affaire.partenaire && (
                    <CardContact
                      nom={affaire?.nom_from_partenaire?.[0] ?? ""}
                      prenom={affaire?.prenom_from_partenaire?.[0] ?? ""}
                      telephone={affaire?.telephone_from_partenaire?.[0] ?? ""}
                      label="partenaire"
                    />
                  )}
      </div>

      {/* demande de rdv */}
      <div className="mt-8">
        <h5 className="flex-auto truncate text-sm font-semibold leading-6 text-gray-700">
          <b>Demande de rendez-vous</b>
        </h5>

        {demandeRdv?.length ? (
          <CardRdv
            date_creation={demandeRdv[0].fields?.date_creation}
            date={demandeRdv[0].fields?.date}
            heure_debut={demandeRdv[0].fields?.heure_debut}
            heure_fin={demandeRdv[0].fields?.heure_fin}
            type_rdv={demandeRdv[0].fields?.type}
            statut={demandeRdv[0].fields?.statut}
            id={demandeRdv[0].id}
          />
        ) : (
          <p className="text-sm text-gray-500">
            Aucune demande de rendez-vous prévu
          </p>
        )}
      </div>

      {/* Rendez-vous notaire + Rendez-vous de signature */}
      <div className="mt-8">
        <h5 className="flex-auto truncate text-sm font-semibold leading-6 text-gray-700">
          <b>Rendez-vous</b>
        </h5>
        <RdvAffaire
          type={type}
          idAffaire={affaireId}
          notaireAirtableId={affaire?.notaire?.[0] ?? affaire?.notaire_link?.[0] ?? null}
          affaire={affaire}
          rdvNotaire={rdvNotaire}
          rdvSignature={rdvSignature}
          refreshData={refreshAll}
        />
      </div>

      {/* documents */}
      <div className="mt-8">
        <h5 className="flex-auto truncate text-sm font-semibold leading-6 text-gray-700 mb-3">
          <b>Documents déposés</b>
        </h5>

        <CardAvancementDocument
          statut={
            action?.find((r) => r.fields?.nom === "Ajout document")?.fields
              ?.statut ?? "Non trouvé"
          }
          onOpenDocuments={onOpenDocuments}
        />
      </div>

      {/* informations à compléter */}
      <div className="mt-8">
        <h5 className="flex-auto truncate text-sm font-semibold leading-6 text-gray-700">
          <b>Informations à compléter</b>
        </h5>

        {action
          .filter(
            (col) =>
              (col.fields?.nom || "").toLowerCase() !== "ajout document"
          )
          .slice()
          .sort(
            (a, b) =>
              Number(a.fields?.ordre ?? 999) - Number(b.fields?.ordre ?? 999)
          )
          .map((col) => (
            <CardInfoACompleter
              key={col.id}
              nom_document={col.fields?.nom}
              statut={col.fields?.statut}
              action_id={col.id}
              affaire={affaire}
            />
          ))}
      </div>
    </>
  );
  }
  

  return <></>;

}

export default TabsAffaireInformations;